import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { UploadQueueService } from '@simOn/common/upload-queue/services';
import { SimIconComponent } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { Subject, take, takeUntil } from 'rxjs';
import { SidenavRightPortalService } from '../sidenav-right-portal/sidenav-right-portal.service';
import { OperationsInProgressPanelComponent } from './operations-in-progress-panel.component';

/**
 * Navbar button informing about background operations (file uploads, packing, etc.).
 * Shows a spinner while anything runs, or an error summary once operations finished
 * with failures; opens the right-side panel with details.
 */
@Component({
  selector: 'layout-operations-in-progress',
  templateUrl: './operations-in-progress-button.component.html',
  styleUrl: './operations-in-progress-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SimIconComponent, SimSpinnerComponent]
})
export class OperationsInProgressButtonComponent {
  private readonly _uploadQueue = inject(UploadQueueService);
  private readonly _sidenavRightPortal = inject(SidenavRightPortalService);
  private readonly _panelDestroy = new Subject<void>();
  private readonly _cdr = inject(ChangeDetectorRef);

  protected readonly inProgressCount = computed(
    () => (this._uploadQueue.queuedFilesState() ?? []).filter((element) => element.state === 'IN_PROGRESS').length
  );

  protected readonly errorCount = computed(
    () => (this._uploadQueue.queuedFilesState() ?? []).filter((element) => element.state === 'ERROR').length
  );

  protected openPanel(): void {
    const ref = this._sidenavRightPortal.open(OperationsInProgressPanelComponent, {
      width: 'min(520px, 100%)',
      hasBackdrop: true
    });

    this._panelDestroy.next();
    this._closeOnBackdropDismiss();

    if (ref) {
      outputToObservable(ref.instance.closePanel)
        .pipe(take(1), takeUntil(this._panelDestroy))
        .subscribe(() => this.closePanel());

      this._cdr.markForCheck();
    }
  }

  closePanel(): void {
    this._panelDestroy.next();
    this._sidenavRightPortal.close();

    this._cdr.markForCheck();
  }

  private _closeOnBackdropDismiss(): void {
    this._sidenavRightPortal
      .sidenavClosed$()
      .pipe(take(1), takeUntil(this._panelDestroy))
      .subscribe(() => this.closePanel());
  }
}
